import { type FC, useRef, useState } from "react";

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogBody,
  DialogTitle,
} from "@/shared/components/ui/dialog";
import { Button } from "@/shared/components/ui/button";
import { FieldError } from "@/shared/components/ui/FieldError";
import ConfirmDeleteDialog from "@/shared/components/overlay/ConfirmDeleteDialog";
import { useUploadSupportingDocument } from "@/shared/hooks/useUploadSupportingDocument";
import type { EstateItem, CreateEstateItemRequest } from "@/shared/types/estateTypes";

import {
  type EditingState,
  parseItem,
  getDialogTitle,
  getDeleteTitle,
  getFileFromForm,
  validateFormFields,
  buildUpdatePayload,
} from "./categoryEditHelpers";
import { CategoryEditForm } from "./CategoryEditForm";

interface CategoryEditDialogProps {
  item: EstateItem | null;
  isOpen: boolean;
  onClose: () => void;
  updateItem: (id: number, updates: Partial<CreateEstateItemRequest>) => Promise<unknown>;
  isUpdatingItem: boolean;
  deleteItem: (id: number) => Promise<unknown>;
  isDeletingItem: boolean;
}

export const CategoryEditDialog: FC<CategoryEditDialogProps> = ({
  item,
  isOpen,
  onClose,
  updateItem,
  isUpdatingItem,
  deleteItem,
  isDeletingItem,
}) => {
  const { upload, isUploading } = useUploadSupportingDocument();
  const dialogRef = useRef<HTMLDivElement | null>(null);
  const [editing, setEditing] = useState<EditingState | null>(() =>
    item ? parseItem(item) : null,
  );
  const [existingFileName, setExistingFileName] = useState<string | null>(
    item && !item.extracted_from_document ? (item.source_document?.file_name ?? null) : null,
  );
  const [fileRemoved, setFileRemoved] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<Partial<Record<string, string>>>({});
  const [serverError, setServerError] = useState<string | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const handleSave = async () => {
    if (!editing || !item) throw new Error("handleSave called without editing state or item");

    const errors = validateFormFields(editing);
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return;
    }

    setFieldErrors({});

    try {
      let sourceDocumentId: number | undefined;

      const fileInfo = getFileFromForm(editing);
      if (fileInfo) {
        if (!item.estate_id) throw new Error("Unable to upload file");
        sourceDocumentId = await upload(fileInfo.file, item.estate_id, fileInfo.docType);
      }

      const sourceDocUpdate = sourceDocumentId
        ? { source_document_id: sourceDocumentId }
        : fileRemoved
          ? { source_document_id: null }
          : {};

      const payload = buildUpdatePayload(editing);
      await updateItem(item.id, { ...payload, ...sourceDocUpdate });
      onClose();
    } catch (e) {
      setServerError(e instanceof Error ? e.message : "Failed to save changes");
    }
  };

  const handleDelete = async () => {
    if (!item) throw new Error("handleDelete called without item");
    try {
      await deleteItem(item.id);
      setShowDeleteConfirm(false);
      onClose();
    } catch {
      setServerError("Failed to delete item");
    }
  };

  const updateForm = (newForm: EditingState["form"]) => {
    setFieldErrors((prev) => {
      if (!Object.keys(prev).length) return prev;
      const next = { ...prev };
      const currentForm = (editing?.form ?? {}) as unknown as Record<string, unknown>;
      const updatedForm = newForm as unknown as Record<string, unknown>;
      for (const k of Object.keys(next)) {
        if (updatedForm[k] !== currentForm[k]) delete next[k];
      }
      return next;
    });
    setServerError(null);
    setEditing((prev) => (prev ? ({ ...prev, form: newForm } as EditingState) : null));
  };

  const handleFileRemove = () => {
    setFileRemoved(true);
    setExistingFileName(null);
  };

  return (
    <>
      <Dialog
        open={isOpen}
        onOpenChange={(open) => {
          if (!open) onClose();
        }}
      >
        <DialogContent
          ref={dialogRef}
          size="lg"
          onPointerDownOutside={(e) => {
            const target = e.target as HTMLElement;
            if (target.closest("[data-address-dropdown]")) {
              e.preventDefault();
            }
          }}
          onFocusOutside={(e) => {
            const related = e.detail.originalEvent.relatedTarget as HTMLElement | null;
            if (related?.closest("[data-address-dropdown]")) {
              e.preventDefault();
            }
          }}
        >
          <DialogHeader>
            <DialogTitle>
              {editing ? getDialogTitle(editing.category, item?.item_type ?? undefined) : "Edit"}
            </DialogTitle>
          </DialogHeader>
          <DialogBody>
            {editing && (
              <CategoryEditForm
                editing={editing}
                existingFileName={fileRemoved ? null : existingFileName}
                onFormChange={updateForm}
                onFileRemove={handleFileRemove}
                portalContainer={dialogRef.current}
                fieldErrors={fieldErrors}
              />
            )}
          </DialogBody>
          <div className="flex flex-col gap-3 pt-6">
            {serverError && <FieldError message={serverError} />}
            <div className="flex justify-between">
              <Button
                type="button"
                variant="destructive"
                disabled={isDeletingItem || isUpdatingItem || isUploading}
                onClick={() => setShowDeleteConfirm(true)}
              >
                Delete
              </Button>
              <Button
                disabled={isUpdatingItem || isDeletingItem || isUploading}
                onClick={handleSave}
              >
                {isUploading ? "Uploading..." : isUpdatingItem ? "Saving..." : "Update"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <ConfirmDeleteDialog
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={handleDelete}
        title={
          editing ? getDeleteTitle(editing.category, item?.item_type ?? undefined) : "Delete Item"
        }
        description={`Are you sure you want to delete "${item?.item_name}"? This action cannot be undone.`}
        isLoading={isDeletingItem}
      />
    </>
  );
};
